import React, { useMemo, useState } from "react";
import Select from "react-select";
import { Package, User } from "react-feather";
import { useGetCustomersQuery } from "../../masters/customers/customersApi";
import ApprovedQuotationPicker from "../components/ApprovedQuotationPicker";

// Purchase type per LPO line (Batch219, 2026-10-28). Customer: "purchase type selection should
// be with the product (per line) ... buy for stock and customers (1 customer, 2 customers) on
// the same LPO". A line is warehouse STOCK or a CUSTOMER job (customer + optional approved
// quotation). The same product may sit on several lines when the allocation differs. A Stock-In
// against the LPO copies each line's allocation (server-side); received customer units still go
// into the branch stock balance. Internal only - the supplier copy (print/email) never shows it.

export const PURCHASE_TYPES = {
  STOCK: { value: "STOCK", label: "Warehouse Stock", icon: Package, color: "#1565C0", bg: "#E3F2FD" },
  CUSTOMER: { value: "CUSTOMER", label: "Customer Job", icon: User, color: "#AD1457", bg: "#FCE4EC" },
};

export const purchaseTypeOf = (line) => (line?.purchaseType || line?.linePurchaseType) === "CUSTOMER" ? "CUSTOMER" : "STOCK";

/** "Stock", "Customer Job" or "Mixed: Stock + Customer Job" - what the lines say the LPO is for. */
export const derivedLpoPurpose = (lines = []) => {
  const types = new Set(lines.map(purchaseTypeOf));
  if (types.has("CUSTOMER") && types.has("STOCK")) return { key: "MIXED", label: "Mixed: Stock + Customer Job" };
  if (types.has("CUSTOMER")) return { key: "CUSTOMER", label: "Customer Job" };
  return { key: "STOCK", label: "Warehouse Stock" };
};

export const PurchaseTypePill = ({ type }) => {
  const p = PURCHASE_TYPES[type] || PURCHASE_TYPES.STOCK;
  const Icon = p.icon;
  return (
    <span
      className="badge d-inline-flex align-items-center gap-1 text-nowrap"
      style={{ background: p.bg, color: p.color, border: `1px solid ${p.color}55`, fontWeight: 600 }}
    >
      <Icon size={12} /> {p.label}
    </span>
  );
};

/** Saved line -> "Customer / Approved Work Order" cell. */
export const AllocationTarget = ({ line }) =>
  purchaseTypeOf(line) === "CUSTOMER" ? (
    <span>
      <span className="tx-party">{line.customerName || `Customer ${line.custId}`}</span>
      {line.quotationNumber && <span className="text-muted"> / {line.quotationNumber}</span>}
    </span>
  ) : (
    <span className="text-muted">Main Warehouse (Stock Replenishment)</span>
  );

const MAX_SHOWN = 200;

// Every active customer the caller can see, loaded once and filtered as you type (react-select's
// own filtering - the "type and search in the dropdown" convention), capped at 200 shown so a
// 4,000-customer branch list still opens instantly.
const CustomerSelect = ({ options, value, onChange, disabled }) => {
  const [input, setInput] = useState("");
  const shown = useMemo(() => {
    const q = input.trim().toLowerCase();
    const matches = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
    return matches.slice(0, MAX_SHOWN);
  }, [options, input]);
  return (
    <Select
      classNamePrefix="react-select"
      options={shown}
      filterOption={null}
      inputValue={input}
      onInputChange={(v, { action }) => {
        if (action === "input-change") setInput(v);
        else if (action === "set-value" || action === "menu-close") setInput("");
      }}
      value={value}
      onChange={onChange}
      isDisabled={disabled}
      isClearable
      placeholder="Customer..."
      noOptionsMessage={() => (input ? "No matching customers" : "No customers")}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
    />
  );
};

export const toCustomerOption = (c) => ({
  value: `${c.branchId}-${c.custId}`,
  label: c.branchCode ? `${c.custName} (${c.branchCode})` : c.custName,
  customer: { custId: c.custId, branchId: c.branchId, custName: c.custName },
});

/** Loads the customer list for the allocation pickers (skipped until a line needs it). */
export const useAllocationCustomerOptions = (needed) => {
  const { data = [] } = useGetCustomersQuery({ isActive: true }, { skip: !needed });
  return useMemo(() => data.map(toCustomerOption).sort((a, b) => a.label.localeCompare(b.label)), [data]);
};

/**
 * The editable "Purchase Type / Customer" cells for LineItemsEditor's extraColumns slot.
 * `defaultCustomer` (the header's customer, when one is picked) fills a line switched to
 * Customer Job.
 */
export const allocationExtraColumns = ({ customerOptions, defaultCustomer, defaultQuotationRecordGuid }) => ({
  headers: [
    { key: "purchaseType", label: "Purchase Type", style: { width: 160 } },
    { key: "allocation", label: "Customer / Approved Work Order", style: { minWidth: 260 } },
  ],
  renderCells: (row, updateRow, disabled) => {
    const type = purchaseTypeOf(row);
    return [
      <td key="purchaseType">
        <select
          className="form-control"
          value={type}
          disabled={disabled}
          onChange={(e) =>
            updateRow(
              row.key,
              e.target.value === "CUSTOMER"
                ? {
                    purchaseType: "CUSTOMER",
                    custOption: row.custOption || defaultCustomer || null,
                    quotationRecordGuid: row.custOption ? row.quotationRecordGuid || "" : defaultCustomer ? defaultQuotationRecordGuid || "" : "",
                  }
                : { purchaseType: "STOCK", custOption: null, quotationRecordGuid: "" }
            )
          }
        >
          <option value="STOCK">{PURCHASE_TYPES.STOCK.label}</option>
          <option value="CUSTOMER">{PURCHASE_TYPES.CUSTOMER.label}</option>
        </select>
      </td>,
      <td key="allocation">
        {type === "CUSTOMER" ? (
          <>
            <CustomerSelect
              options={customerOptions}
              value={row.custOption || null}
              disabled={disabled}
              onChange={(opt) => updateRow(row.key, { custOption: opt, quotationRecordGuid: "" })}
            />
            <div className="mt-1">
              <ApprovedQuotationPicker
                custId={row.custOption?.customer?.custId}
                customerBranchId={row.custOption?.customer?.branchId}
                value={row.quotationRecordGuid || ""}
                onChange={(guid) => updateRow(row.key, { quotationRecordGuid: guid || "" })}
                disabled={disabled}
              />
            </div>
          </>
        ) : (
          <span className="text-muted small">Main Warehouse (Stock Replenishment)</span>
        )}
      </td>,
    ];
  },
});

/** Read-only allocation cells (Stock-In form rows carry the LPO line's allocation). */
export const readOnlyAllocationColumns = {
  headers: [{ key: "allocation", label: "Allocation", style: { minWidth: 220 } }],
  renderCells: (row) => [
    <td key="allocation">
      {row.refId ? (
        <>
          <PurchaseTypePill type={purchaseTypeOf(row)} />
          <div className="small mt-1"><AllocationTarget line={row} /></div>
        </>
      ) : (
        <span className="text-muted small">-</span>
      )}
    </td>,
  ],
};

const qty = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

/**
 * "Stock Allocation Summary": one blue tile for warehouse stock (total and the per-line split,
 * e.g. "4 units (2 + 2)") and one pink tile per customer / quotation.
 */
export const StockAllocationSummary = ({ lines = [], qtyField = "orderedQty" }) => {
  const stockLines = lines.filter((l) => purchaseTypeOf(l) === "STOCK");
  const customers = new Map();
  lines.filter((l) => purchaseTypeOf(l) === "CUSTOMER").forEach((l) => {
    const key = `${l.custBranchId}-${l.custId}-${l.quotationRecordGuid || ""}`;
    const entry = customers.get(key) || { name: l.customerName || `Customer ${l.custId}`, quotation: l.quotationNumber, qtys: [] };
    entry.qtys.push(Number(l[qtyField]) || 0);
    customers.set(key, entry);
  });
  const sum = (arr) => arr.reduce((a, b) => a + b, 0);
  const split = (arr) => (arr.length > 1 ? ` (${arr.map(qty).join(" + ")})` : "");
  const units = (n) => `${qty(n)} unit${n === 1 ? "" : "s"}`;
  const stockQtys = stockLines.map((l) => Number(l[qtyField]) || 0);

  const tile = (p, title, body, key) => (
    <div key={key} className="col-md-6 col-xl-4 mb-2">
      <div className="h-100 rounded p-3" style={{ background: p.bg, border: `1px solid ${p.color}40` }}>
        <div className="small fw-semibold" style={{ color: p.color }}>{title}</div>
        {body}
      </div>
    </div>
  );

  return (
    <div className="card h-100 mb-0">
      <div className="card-body">
        <h5 className="mb-3">Stock Allocation Summary</h5>
        <div className="row">
          {stockLines.length > 0 &&
            tile(
              PURCHASE_TYPES.STOCK,
              "Warehouse Stock Allocation",
              <div className="fw-bold fs-5" style={{ color: PURCHASE_TYPES.STOCK.color }}>{units(sum(stockQtys))}{split(stockQtys)}</div>,
              "stock"
            )}
          {[...customers.entries()].map(([key, c]) =>
            tile(
              PURCHASE_TYPES.CUSTOMER,
              "Customer-Job Allocation",
              <>
                <div className="fw-bold fs-5" style={{ color: PURCHASE_TYPES.CUSTOMER.color }}>{units(sum(c.qtys))}{split(c.qtys)}</div>
                <div className="tx-party">{c.name}</div>
                {c.quotation && <div className="small text-muted">{c.quotation}</div>}
              </>,
              key
            )
          )}
        </div>
      </div>
    </div>
  );
};
